import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';

const CE_QUERY = gql`
  query AdminPlanBuilderCeReport($limitExceptions: Int) {
    adminPlanBuilderCeReport(limitExceptions: $limitExceptions) {
      total_eligible_templates
      total_completed_plans
      total_assessment_attempts
      total_passed_credits
      total_credits_issued
      templates {
        planTemplate {
          id
          name
          slug
          planType
          status
        }
        started_count
        in_progress_count
        paused_count
        completed_count
        completion_rate
      }
      exceptions {
        completedAt
        user {
          id
          name
          email
        }
        planTemplate {
          id
          name
          slug
        }
      }
    }
  }
`;

const pct = (v) => `${Math.round((v || 0) * 1000) / 10}%`;

const PlanBuilderCeReport = () => {
  const [limitExceptions, setLimitExceptions] = useState(50);
  const { data, loading, error } = useQuery(CE_QUERY, {
    variables: { limitExceptions: Number(limitExceptions) || 50 },
    fetchPolicy: 'cache-and-network',
  });

  const report = data?.adminPlanBuilderCeReport;

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Plan Builder CE Outcomes</Typography>

      {error && <Typography color="error">Error: {error.message}</Typography>}
      {loading && <Typography color="textSecondary">Loading...</Typography>}

      {report && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography color="textSecondary" variant="subtitle2">CE eligible templates</Typography>
                <Typography variant="h5">{report.total_eligible_templates}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography color="textSecondary" variant="subtitle2">Completed CE plans</Typography>
                <Typography variant="h5">{report.total_completed_plans}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography color="textSecondary" variant="subtitle2">Assessment attempts</Typography>
                <Typography variant="h5">{report.total_assessment_attempts}</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card><CardContent>
                <Typography color="textSecondary" variant="subtitle2">Passed credits</Typography>
                <Typography variant="h5">{report.total_passed_credits}</Typography>
                <Typography variant="body2" color="textSecondary">Credits issued: {report.total_credits_issued}</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>

          <Box mt={3}>
            <Typography variant="h6" gutterBottom>CE templates (progress)</Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Template</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Started</TableCell>
                    <TableCell align="right">Completed</TableCell>
                    <TableCell align="right">Completion rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.templates.map((t) => (
                    <TableRow key={t.planTemplate.id} hover>
                      <TableCell>{t.planTemplate.name}</TableCell>
                      <TableCell>{t.planTemplate.status}</TableCell>
                      <TableCell>{t.planTemplate.planType}</TableCell>
                      <TableCell align="right">{t.started_count}</TableCell>
                      <TableCell align="right">{t.completed_count}</TableCell>
                      <TableCell align="right">{pct(t.completion_rate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" gutterBottom>Exceptions (completed plan, missing passed credit)</Typography>
            <Box mb={1} maxWidth={240}>
              <TextField
                label="Limit"
                type="number"
                value={limitExceptions}
                onChange={(e) => setLimitExceptions(e.target.value)}
                fullWidth
              />
            </Box>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Completed at</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.exceptions.map((ex, idx) => (
                    <TableRow key={`${ex.user.id}-${ex.planTemplate.id}-${idx}`} hover>
                      <TableCell>
                        {ex.user.name || `User ${ex.user.id}`}
                        {ex.user.email ? (
                          <Typography variant="caption" display="block" color="textSecondary">{ex.user.email}</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{ex.planTemplate.name || ex.planTemplate.slug}</TableCell>
                      <TableCell>{ex.completedAt ? new Date(ex.completedAt).toLocaleString() : '—'}</TableCell>
                    </TableRow>
                  ))}
                  {!report.exceptions.length && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography color="textSecondary">No exceptions found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PlanBuilderCeReport;


